import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  Switch,
  Typography,
  Space,
  message,
  Row,
  Col,
  Divider,
  Tag,
  Button,
} from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import client from "../helpers/directusClient";
import { readItems, updateItem, createItem } from "@directus/sdk";

const { Title, Text } = Typography;

const RoleManagement = () => {
  const { user, company } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({});

  // Define roles and their permissions
  const roles = [
    { key: "admin", name: "Administrator", description: "Full system access" },
    {
      key: "loan_officer",
      name: "Loan Officer",
      description: "Manages loans and borrowers",
    },
    {
      key: "branch_manager",
      name: "Branch Manager",
      description: "Manages branch operations",
    },
    {
      key: "credit_manager",
      name: "Credit Manager",
      description: "Handles credit decisions",
    },
  ];

  // Define available permissions
  const availablePermissions = [
    {
      key: "view_reports",
      name: "View Reports",
      description: "Access to all system reports",
      category: "Reports",
    },
    {
      key: "manage_users",
      name: "Manage Users",
      description: "Create, edit, and delete users",
      category: "User Management",
    },
    {
      key: "manage_loans",
      name: "Manage Loans",
      description: "Create and modify loan applications",
      category: "Loan Management",
    },
    {
      key: "view_financial_data",
      name: "View Financial Data",
      description: "Access to financial reports and analytics",
      category: "Financial",
    },
    {
      key: "manage_branches",
      name: "Manage Branches",
      description: "Create and modify branch information",
      category: "Administration",
    },
    {
      key: "manage_loan_products",
      name: "Manage Loan Products",
      description: "Create and modify loan products",
      category: "Product Management",
    },
    {
      key: "view_borrower_profiles",
      name: "View Borrower Profiles",
      description: "Access to borrower personal information",
      category: "Borrower Management",
    },
    {
      key: "manage_collateral",
      name: "Manage Collateral",
      description: "Register and manage collateral items",
      category: "Risk Management",
    },
  ];

  // Fetch role permissions from backend
  const fetchPermissions = async () => {
    setLoading(true);
    try {
      // Try to fetch existing role permissions from your backend
      // For now, we'll check if there's a role_permissions table/collection
      const response = await client
        .request(
          readItems("role_permissions", {
            fields: ["*"],
            filter: {
              company_id: { _eq: company?.id },
            },
          })
        )
        .catch(() => {
          // If table doesn't exist, we'll use default permissions
          console.log("Role permissions table not found, using defaults");
          return [];
        });

      if (response && response.length > 0) {
        // Parse existing permissions
        const existingPermissions = {};
        response.forEach((item) => {
          existingPermissions[item.role_key] = JSON.parse(item.permissions);
        });
        setPermissions(existingPermissions);
      } else {
        // Set default permissions
        const defaultPermissions = {
          admin: {
            view_reports: true,
            manage_users: true,
            manage_loans: true,
            view_financial_data: true,
            manage_branches: true,
            manage_loan_products: true,
            view_borrower_profiles: true,
            manage_collateral: true,
          },
          loan_officer: {
            view_reports: false, // Hide reports for loan officers
            manage_users: false,
            manage_loans: true,
            view_financial_data: false,
            manage_branches: false,
            manage_loan_products: false,
            view_borrower_profiles: true,
            manage_collateral: true,
          },
          branch_manager: {
            view_reports: true,
            manage_users: false,
            manage_loans: true,
            view_financial_data: true,
            manage_branches: false,
            manage_loan_products: true,
            view_borrower_profiles: true,
            manage_collateral: true,
          },
          credit_manager: {
            view_reports: true,
            manage_users: false,
            manage_loans: true,
            view_financial_data: true,
            manage_branches: false,
            manage_loan_products: false,
            view_borrower_profiles: true,
            manage_collateral: true,
          },
        };
        setPermissions(defaultPermissions);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      message.error("Failed to load role permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [company?.id]);

  // Handle permission toggle
  const handlePermissionToggle = (roleKey, permissionKey, checked) => {
    // Don't allow modifying admin permissions
    if (roleKey === "admin") {
      message.warning("Administrator permissions cannot be modified");
      return;
    }

    setPermissions((prev) => ({
      ...prev,
      [roleKey]: {
        ...prev[roleKey],
        [permissionKey]: checked,
      },
    }));
  };

  // Save permissions to backend as JSON
  const savePermissions = async () => {
    setSaving(true);
    try {
      // Save each role's permissions as JSON to the backend
      for (const roleKey of Object.keys(permissions)) {
        const permissionData = {
          company_id: company?.id,
          role_key: roleKey,
          permissions: JSON.stringify(permissions[roleKey]),
          updated_at: new Date().toISOString(),
        };

        try {
          // Try to update existing record
          await client.request(
            updateItem(
              "role_permissions",
              `${company?.id}_${roleKey}`,
              permissionData
            )
          );
        } catch (updateError) {
          // If update fails, create new record
          await client.request(
            createItem("role_permissions", {
              id: `${company?.id}_${roleKey}`,
              ...permissionData,
            })
          );
        }
      }

      message.success("Role permissions saved successfully");
    } catch (error) {
      console.error("Error saving permissions:", error);
      message.error(
        "Failed to save permissions. Using localStorage as fallback."
      );

      // Fallback to localStorage
      localStorage.setItem(
        `rolePermissions_${company?.id}`,
        JSON.stringify(permissions)
      );
      message.info("Permissions saved locally");
    } finally {
      setSaving(false);
    }
  };

  // Check if user can access this page
  if (user?.role !== "Administrator" && user?.role !== "Branch Manager") {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Title level={3}>Access Denied</Title>
        <Text>You don't have permission to access role management.</Text>
      </div>
    );
  }

  // Table columns for permissions
  const columns = [
    {
      title: "Permission",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.description}
          </Text>
        </Space>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    ...roles.map((role) => ({
      title: (
        <Space direction="vertical" size={0} style={{ textAlign: "center" }}>
          <Text strong>{role.name}</Text>
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {role.description}
          </Text>
        </Space>
      ),
      key: role.key,
      align: "center",
      render: (_, record) => (
        <Switch
          checked={permissions[role.key]?.[record.key] || false}
          onChange={(checked) =>
            handlePermissionToggle(role.key, record.key, checked)
          }
          size="small"
          disabled={role.key === "admin"} // Disable admin modifications
        />
      ),
    })),
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <Title level={2} style={{ margin: 0 }}>
                    Role Management
                  </Title>
                  <Text type="secondary">
                    Configure permissions for different user roles. Permissions
                    are stored as JSON.
                  </Text>
                </div>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchPermissions}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={savePermissions}
                    loading={saving}
                  >
                    Save Changes
                  </Button>
                </Space>
              </div>

              <Divider />

              <div>
                <Title level={4}>Permission Matrix</Title>
                <Text type="secondary">
                  Toggle permissions for each role. Administrator permissions
                  are locked and cannot be modified. Loan officers have reports
                  disabled by default.
                </Text>
              </div>

              <Table
                columns={columns}
                dataSource={availablePermissions}
                rowKey="key"
                loading={loading}
                pagination={false}
                scroll={{ x: 1000 }}
                size="middle"
              />

              <div style={{ marginTop: "16px" }}>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  <strong>Note:</strong> Permissions are stored as JSON in the
                  backend. Changes will be applied immediately but users may
                  need to refresh their session. Administrator role permissions
                  are locked for security.
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RoleManagement;
